import { useTranslation } from 'react-i18next';
import type { Client } from '@hero/schema';
import { useEditClientLinkManager } from '@hero/web-hooks';
import { ModalForm } from '../../../app/Modal';
import { Button } from '../../../ui/Button/Button';
import { EntityDirectoryPicker } from '../../EntityDirectory/Picker/EntityDirectoryPicker';

export interface EditClientLinkModalProps {
  client: Client | undefined;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Per-section modal for linking / unlinking a shared network identity
 * (`Client.linkedParty`) to a `Client`. Wraps the unified
 * {@link EntityDirectoryPicker} so the user can pick either a global
 * Brand Directory `Profile` or a signed-up Hero `Org` (and propose a
 * new brand when the directory misses).
 *
 * Linking enables per-client pricing (the workspace Pricing tab
 * unlocks once `Client.linkedParty` is set) and richer downstream
 * resolution (Route `Party.ids[]` carries the bridged `profile_id` /
 * `org_id` once a linked-Profile signs up).
 */
export function EditClientLinkModal({
  client,
  isOpen,
  onClose,
}: EditClientLinkModalProps) {
  const { t } = useTranslation(['m-client', 'c-main']);
  const {
    pendingParty,
    isPickerOpen,
    openPicker,
    closePicker,
    onPick,
    reset,
    onSaveLink,
    onUnlink,
    isPending,
    isSuccess,
  } = useEditClientLinkManager(client);

  const currentLinkName = client?.linkedParty?.name;
  const previewName = pendingParty?.name ?? currentLinkName;

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSave = async () => {
    if (!pendingParty) return;
    try {
      await onSaveLink();
    } catch (err) {
      console.error('Failed to link:', err);
    }
  };

  const handleUnlink = async () => {
    try {
      await onUnlink();
    } catch (err) {
      console.error('Failed to unlink:', err);
    }
  };

  return (
    <>
      <ModalForm
        isOpen={isOpen}
        onClose={handleClose}
        title={t('edit_modal.linked_party.title')}
        description={t('edit_modal.linked_party.description')}
        onSave={handleSave}
        isLoading={isPending}
        isSuccess={isSuccess}
        disableSave={!pendingParty}
        saveText={t('c-main:crud_buttons.save')}
        size="md">
        <div className="space-y-4">
          {previewName ? (
            <div className="rounded-md border border-gray-200 px-4 py-3">
              <div className="text-xs font-medium text-muted-foreground mb-1">
                {t('edit_modal.linked_party.current_brand')}
              </div>
              <div className="text-sm font-medium text-gray-900">
                {previewName}
              </div>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={openPicker} disabled={isPending}>
              {t('edit_modal.linked_party.pick_brand')}
            </Button>
            {currentLinkName && !pendingParty ? (
              <Button
                variant="ghost"
                onClick={handleUnlink}
                disabled={isPending}>
                {t('edit_modal.linked_party.unlink')}
              </Button>
            ) : null}
          </div>
        </div>
      </ModalForm>

      <EntityDirectoryPicker
        isOpen={isPickerOpen}
        onClose={closePicker}
        onPick={onPick}
        description={t('edit_modal.linked_party.description')}
      />
    </>
  );
}
